import navigatorAPI from './navigatorAPI'
import {
  __prod__,
  __show_debug_output__,
  apiEndpoints,
} from 'settings/app/constants'
import axios from 'axios'

// black list for xhr cancellation when previous request running
export const CANCELING_PREVIOUS_REQUEST_ROUTES = ['autocomplete']

export const DISTANCE_REQUEST_CACHE = {
  // setter to simplify caching
  // eslint-disable-next-line
  setDistance: function ({ origin, destination, distance }) {
    this[`${ origin.lat },${ origin.lng },${ destination.lat },${ destination.lng }`] =
      distance
  },
  // getter to simplify caching
  // eslint-disable-next-line
  getDistance: function ({ origin, destination }) {
    const propertyName = `${ origin.lat },${ origin.lng },${ destination.lat },${ destination.lng }`
    if (this.hasOwnProperty(propertyName)) {
      return this[propertyName]
    }
  },
}

/**
 * request exact search location distance
 * READ MORE => https://developers.google.com/maps/documentation/javascript/directions
 * @param origin coordinates
 * @param destinations coordinates[]
 * @returns {Promise<string[]>}
 */
export const googleDistanceRequest = async ({ origin, destinations }) =>
  new Promise((resolve) => {
    // https://stackoverflow.com/questions/54857375/display-directions-with-react-google-maps
    // https://developers.google.com/maps/documentation/javascript/overview#Dynamic
    const distances = []
    const requestDestinations = {}
    destinations.forEach((destination, index) => {
      const distance = DISTANCE_REQUEST_CACHE.getDistance({
        origin,
        destination,
      })
      if (distance) {
        distances.push(distance)
      } else {
        requestDestinations[index] = destination
      }
    })

    if (Object.keys(requestDestinations).length === 0) {
      return resolve(distances)
    }

    const distanceService = new google.maps.DistanceMatrixService()
    // distances returned in the same order as provided
    distanceService.getDistanceMatrix(
      {
        origins: [origin],
        destinations: Object.values(requestDestinations),
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result) => {
        Object.keys(requestDestinations).forEach((key, index) => {
          const distance =
            result?.rows.length > 0 &&
            result.rows[0]?.elements.length > 0 &&
            // distance is returned in meters
            Math.round(
              result.rows[0].elements[index].distance.value / 1000
            ).toString()

          if (distance) {
            // cache distance
            DISTANCE_REQUEST_CACHE.setDistance({
              origin,
              destination: requestDestinations[key],
              distance,
            })
            // add distance in same order as provided
            distances.splice(key, 0, distance)
          }
        })
        return resolve(distances)
      }
    )
  })

/**
 * handler function for perform an autocomplete request
 */
export const getAutoCompleteQuery = async ({ endPoint, query }) => {
  try {
    const { data } = await navigatorAPI.get(
      `${ apiEndpoints.autoComplete }${ endPoint }?query=${ encodeURI(query) }`
    )
    if (data) {
      return data.results
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      if (__show_debug_output__ && __prod__) {
        console.log('previous autocomplete request: canceled')
      }
    } else {
      console.error(e)
      return Promise.reject(e)
    }
  }
}

/**
 * get address data with geo localising
 * @param lat
 * @param lng
 * @returns {Promise<*>}
 */
export const getAddressByGeolocation = async ({ lat, lng }) => {
  try {
    const { data } = await navigatorAPI.get(
      `${ apiEndpoints.locationApiUrl }?lat=${ lat }&lng=${ lng }`
    )
    if (data) {
      return data.match
    }
  } catch (e) {
    // console.error(e)
    return Promise.reject(e)
  }
}

/**
 * fetch bookmark data
 * @param type
 * @param ids
 * @param searchParams
 * @param reloadWithOutTreatment
 * @returns {Promise<any>}
 */
export const getCompareGeneralData = async ({
  type,
  ids,
  searchParams,
  reloadWithOutTreatment,
}) => {
  try {
    let params = { ids: ids.join(',') }

    if (!reloadWithOutTreatment) {
      params = {
        ...params,
        ...searchParams,
      }
    }

    const { data } = await navigatorAPI.get(
      `${ apiEndpoints.bookmarks }${ type }/`,
      { params }
    )

    if (data) {
      return data
    }
  } catch (e) {
    return Promise.reject(e)
  }
}

/**
 * get details data
 * @param endpoint
 * @param id
 * @param queryParams
 * @param clientId
 * @returns {Promise<AxiosResponse<any>|boolean>}
 */
export const getDetailsData = async ({
  endpoint,
  id,
  queryParams = '',
  clientId,
}) =>
  await navigatorAPI
    .get(`${ endpoint }${ id }/${ queryParams }`, { params: { clientId } })
    .then((response) => response.data)
    .catch((error) => error)

/**
 * get carehome price calculation data
 * @param endpoint
 * @param id
 * @param queryParams
 * @param clientId
 * @returns {Promise<AxiosResponse<any>|boolean>}
 */
export const getCarePriceData = async ({ endpoint, id, queryParams = '' }) =>
  await navigatorAPI
    .get(
      `${ endpoint }/${ id }/calc${
        queryParams.length > 0 ? '?' : ''
      }${ queryParams }`,
      { params: {} }
    )
    .then((response) => response.data)
    .catch((error) => error)
